import React, { useState, FormEvent, useRef } from 'react';
import {
    Box,
    Typography,
    Paper,
    Grid,
    Card,
    CardContent,
    Divider,
    CircularProgress,
    TextField,
    Button,
    IconButton,
    FormGroup,
    FormControlLabel,
    Checkbox,
    InputAdornment,
    List,
    ListItem,
    ListItemText
} from '@mui/material';
import {
    HiOutlineShieldCheck,
    HiOutlinePlus,
    HiOutlineMinusCircle,
    HiOutlineTrash,
    HiOutlinePencil
} from 'react-icons/hi';

type AdminPermissions = {
    change_settings: boolean;
    change_user_settings: boolean;
    manage_admins: boolean;
    ticket_access: boolean;
};

interface AdminApiResponse {
    id: number;
    username: string;
    first_name: string;
    last_name: string;
    admin: { permissions: AdminPermissions };
}

interface CommissionTier {
    target: number;
    commission_rate: number;
}

interface UserCommission {
    user: { id: number; first_name: string; last_name: string; username: string; };
    data: CommissionTier[];
}

interface CommissionSettings {
    default: CommissionTier[];
    users: UserCommission[];
}

interface SettingsTabProps {
    apiFetch: (url: string, options?: RequestInit) => Promise<any>;
    handleApiCall: (apiCall: Promise<any>, successMessage: string) => Promise<any>;
    showConfirmationModal: (message: string, onConfirm: () => void) => void;
    setError: (error: string | null) => void;
    admins: AdminApiResponse[];
    loadingAdmins: boolean;
    commissionSettings: CommissionSettings;
    loadingSettings: boolean;
    onEditAdmin: (admin: AdminApiResponse) => void;
}

const SettingsTab: React.FC<SettingsTabProps> = ({
    apiFetch,
    handleApiCall,
    showConfirmationModal,
    setError,
    admins,
    loadingAdmins,
    commissionSettings,
    loadingSettings,
    onEditAdmin
}) => {
    const addAdminFormRef = useRef<HTMLFormElement>(null);
    const [addAdminLoading, setAddAdminLoading] = useState(false);
    const [commissionSaveLoading, setCommissionSaveLoading] = useState(false);

    const [newAdminPermissions, setNewAdminPermissions] = useState<AdminPermissions>({
        change_settings: false,
        change_user_settings: false,
        manage_admins: false,
        ticket_access: false
    });

    const [defaultTiers, setDefaultTiers] = useState<Partial<CommissionTier>[]>((commissionSettings.default && commissionSettings.default.length > 0) ? commissionSettings.default : [{ target: 0, commission_rate: 10 }]);
    const [userTiers, setUserTiers] = useState<Partial<CommissionTier>[]>([{ target: 0, commission_rate: 10 }]);
    const [commissionUserId, setCommissionUserId] = useState('');

    React.useEffect(() => {
         setDefaultTiers((commissionSettings.default && commissionSettings.default.length > 0) ? commissionSettings.default : [{ target: 0, commission_rate: 10 }])
    }, [commissionSettings.default])


    const handleAdminAdd = async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setAddAdminLoading(true);
        const formData = new FormData(e.currentTarget);
        const payload = {
            user_id: parseInt(formData.get('user_id') as string),
            permissions: newAdminPermissions
        };

        try {
            await handleApiCall(
                apiFetch('/admin/', { method: 'POST', body: JSON.stringify(payload) }),
                'ادمین جدید با موفقیت اضافه شد.'
            );
            if (addAdminFormRef.current) addAdminFormRef.current.reset();
            setNewAdminPermissions({ change_settings: false, change_user_settings: false, manage_admins: false, ticket_access: false });
        } finally {
            setAddAdminLoading(false);
        }
    };

    const handleDeleteAdmin = (admin: AdminApiResponse) => {
        showConfirmationModal(`آیا از حذف ادمین "${admin.username}" (ID: ${admin.id}) مطمئن هستید؟`, () => {
            handleApiCall(apiFetch(`/admin/${admin.id}`, { method: 'DELETE' }), `ادمین ${admin.username} با موفقیت حذف شد.`);
        });
    };

    const handlePermissionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setNewAdminPermissions({ ...newAdminPermissions, [event.target.name]: event.target.checked });
    };

    const handleTierChange = (index: number, field: 'target' | 'commission_rate', value: string, type: 'default' | 'user') => {
        const tiers = type === 'default' ? [...defaultTiers] : [...userTiers];
        const numericValue = value === '' ? undefined : Number(value);
        tiers[index] = { ...tiers[index], [field]: numericValue };
        if (type === 'default') setDefaultTiers(tiers);
        else setUserTiers(tiers);
    };

    const addTier = (type: 'default' | 'user') => {
        if (type === 'default') setDefaultTiers([...defaultTiers, { target: undefined, commission_rate: undefined }]);
        else setUserTiers([...userTiers, { target: undefined, commission_rate: undefined }]);
    };

    const removeTier = (index: number, type: 'default' | 'user') => {
        if (type === 'default' && defaultTiers.length > 1) setDefaultTiers(defaultTiers.filter((_, i) => i !== index));
        else if (type === 'user' && userTiers.length > 1) setUserTiers(userTiers.filter((_, i) => i !== index));
    };

    const handleSaveCommission = async (type: 'default' | 'user') => {
        const tiers = type === 'default' ? defaultTiers : userTiers;
        if (type === 'user' && !commissionUserId) { setError('برای ثبت کمیسیون خاص، شناسه کاربر الزامی است.'); return; }
        const cleanedTiers = tiers.map(t => ({ target: Number(t.target), commission_rate: Number(t.commission_rate) }))
                                 .filter(t => !isNaN(t.target) && !isNaN(t.commission_rate) && t.target >= 0 && t.commission_rate >= 0);
        if (cleanedTiers.length === 0) { setError('حداقل یک پله کمیسیون معتبر باید وارد شود.'); return; }
        const targets = new Set(cleanedTiers.map(t => t.target));
        if (targets.size !== cleanedTiers.length) { setError("مقادیر 'تعداد زیرمجموعه' (Target) نمی‌توانند تکراری باشند."); return; }
        if (cleanedTiers.some(t => t.commission_rate > 100)) { setError('مقدار درصد کمیسیون نباید بیشتر از 100 باشد.'); return; }
        cleanedTiers.sort((a, b) => a.target - b.target);

        let payload: any;
        let successMessage: string;
        if (type === 'default') {
            payload = { user_id: null, data: cleanedTiers };
            successMessage = 'تنظیمات پیشفرض کمیسیون با موفقیت ذخیره شد.';
        } else {
            payload = { user_id: Number(commissionUserId), data: cleanedTiers };
            successMessage = `تنظیمات کمیسیون برای کاربر ${commissionUserId} با موفقیت ذخیره شد.`;
        }

        setCommissionSaveLoading(true);
        try {
            await handleApiCall(apiFetch('/settings/commission-rate', { method: 'PUT', body: JSON.stringify(payload) }), successMessage);
            if (type === 'user') { setCommissionUserId(''); setUserTiers([{ target: 0, commission_rate: 10 }]); }
        } finally {
            setCommissionSaveLoading(false);
        }
    };

    const handleEditUserCommission = (userCommission: UserCommission) => {
        setCommissionUserId(String(userCommission.user.id));
        setUserTiers(userCommission.data.length > 0 ? userCommission.data : [{ target: 0, commission_rate: 10 }]);
    };
    
    const handleDeleteUserCommission = (userId: number) => {
        showConfirmationModal(`آیا از حذف کمیسیون خاص برای کاربر با ID ${userId} مطمئن هستید؟`, () => {
            handleApiCall(apiFetch('/settings/commission-rate', { method: 'PUT', body: JSON.stringify({ user_id: userId, data: [] }) }), `کمیسیون خاص برای کاربر ${userId} با موفقیت حذف شد.`);
        });
    };

    return (
        <>
            <Paper className='management-card'>
                <h3 className='tab-section-header'><HiOutlineShieldCheck /> مدیریت ادمین‌ها</h3>
                <Typography variant='h6' sx={{ mt: 4, mb: 2 }}>لیست ادمین‌ها</Typography>
                {loadingAdmins ? <CircularProgress /> : admins.length > 0 ? (
                    <Grid container spacing={2}>
                        {admins.map(admin => (
                            <Grid item xs={12} sm={6} md={4} key={admin.id}>
                                <Card className='management-card' sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                                    <CardContent sx={{ flexGrow: 1 }}>
                                        <Typography variant='h6' component='div'>{admin.first_name} {admin.last_name}</Typography>
                                        <Typography sx={{ mb: 1.5 }}>نام کاربری: {admin.username}</Typography>
                                        <Divider sx={{ my: 1 }} />
                                        <Typography variant='body2'><strong>آیدی کاربر (ادمین):</strong> {admin.id}</Typography>
                                    </CardContent>
                                    <Box sx={{ p: 1, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                                        <IconButton color='primary' size='small' onClick={() => onEditAdmin(admin)}><HiOutlinePencil /></IconButton>
                                        <IconButton color='error' size='small' onClick={() => handleDeleteAdmin(admin)}><HiOutlineTrash /></IconButton>
                                    </Box>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                ) : (<Typography>هیچ ادمینی برای نمایش وجود ندارد.</Typography>)}
                <Divider sx={{ my: 4 }} />
                <form onSubmit={handleAdminAdd} ref={addAdminFormRef}>
                    <Typography variant='h6' sx={{ mt: 2, mb: 2 }}>افزودن ادمین جدید</Typography>
                    <div className='simple-input-group'><label>شناسه کاربر (User ID)</label><input type='number' name='user_id' required /></div>
                    <div className='simple-input-group'>
                        <label>دسترسی‌ها</label>
                        <FormGroup sx={{ display: 'flex', flexDirection: 'row', gap: 2, flexWrap: 'wrap' }}>
                            <FormControlLabel control={<Checkbox checked={newAdminPermissions.change_settings} onChange={handlePermissionChange} name='change_settings'/>} label='تغییر تنظیمات'/>
                            <FormControlLabel control={<Checkbox checked={newAdminPermissions.change_user_settings} onChange={handlePermissionChange} name='change_user_settings'/>} label='تغییر تنظیمات کاربر'/>
                            <FormControlLabel control={<Checkbox checked={newAdminPermissions.manage_admins} onChange={handlePermissionChange} name='manage_admins'/>} label='مدیریت ادمین‌ها'/>
                            <FormControlLabel control={<Checkbox checked={newAdminPermissions.ticket_access} onChange={handlePermissionChange} name='ticket_access'/>} label='دسترسی به تیکت‌ها'/>
                        </FormGroup>
                    </div>
                    <Button type='submit' variant='contained' disabled={addAdminLoading}>{addAdminLoading ? <CircularProgress size={24} /> : 'افزودن ادمین'}</Button>
                </form>
            </Paper>

            <Paper className='management-card'>
                <h3 className='tab-section-header'>مدیریت کمیسیون زیرمجموعه‌گیری</h3>
                <Grid container spacing={5}>
                    <Grid item xs={12} md={6}>
                        <Typography variant='h6' gutterBottom>تنظیمات پیشفرض</Typography>
                        <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>این تنظیمات برای تمام کاربرانی که تنظیمات خاصی برایشان ثبت نشده اعمال می‌شود.</Typography>
                        {defaultTiers.map((tier, index) => (
                            <Box key={index} sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
                                <TextField label='تعداد زیرمجموعه (Target)' type='number' value={tier.target ?? ''} onChange={e => handleTierChange(index, 'target', e.target.value, 'default')} fullWidth required/>
                                <TextField label='درصد کمیسیون' type='number' value={tier.commission_rate ?? ''} onChange={e => handleTierChange(index, 'commission_rate', e.target.value, 'default')} fullWidth required InputProps={{ endAdornment: <InputAdornment position='end'>%</InputAdornment> }}/>
                                <IconButton color='error' onClick={() => removeTier(index, 'default')} disabled={defaultTiers.length <= 1}><HiOutlineMinusCircle /></IconButton>
                            </Box>
                        ))}
                        <Button startIcon={<HiOutlinePlus />} onClick={() => addTier('default')} sx={{ mr: 2 }}>افزودن سطح</Button>
                        <Button variant='contained' onClick={() => handleSaveCommission('default')} disabled={commissionSaveLoading}>{commissionSaveLoading ? <CircularProgress size={24} /> : 'ذخیره پیشفرض'}</Button>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Typography variant='h6' gutterBottom>تنظیمات برای کاربر خاص</Typography>
                        <TextField label='شناسه کاربر (User ID)' value={commissionUserId} onChange={e => setCommissionUserId(e.target.value)} fullWidth sx={{ mb: 2 }}/>
                        {userTiers.map((tier, index) => (
                            <Box key={index} sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
                                <TextField label='تعداد زیرمجموعه (Target)' type='number' value={tier.target ?? ''} onChange={e => handleTierChange(index, 'target', e.target.value, 'user')} fullWidth required/>
                                <TextField label='درصد کمیسیون' type='number' value={tier.commission_rate ?? ''} onChange={e => handleTierChange(index, 'commission_rate', e.target.value, 'user')} fullWidth required InputProps={{ endAdornment: <InputAdornment position='end'>%</InputAdornment> }}/>
                                <IconButton color='error' onClick={() => removeTier(index, 'user')} disabled={userTiers.length <= 1}><HiOutlineMinusCircle /></IconButton>
                            </Box>
                        ))}
                        <Button startIcon={<HiOutlinePlus />} onClick={() => addTier('user')} sx={{ mr: 2 }}>افزودن سطح</Button>
                        <Button variant='contained' color='primary' onClick={() => handleSaveCommission('user')} disabled={commissionSaveLoading || !commissionUserId}>{commissionSaveLoading ? <CircularProgress size={24} /> : 'ذخیره برای کاربر'}</Button>
                    </Grid>
                </Grid>
                <Divider sx={{ my: 4 }} />
                <Typography variant='h6' gutterBottom>کاربران با تنظیمات خاص</Typography>
                {loadingSettings ? <CircularProgress /> : (
                    <List>
                        {commissionSettings.users && commissionSettings.users.length > 0 ? (
                            commissionSettings.users.map(userComm => (
                                <ListItem key={userComm.user.id} secondaryAction={
                                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                        <Button variant='outlined' size='small' onClick={() => handleEditUserCommission(userComm)}>ویرایش</Button>
                                        <IconButton color='error' onClick={() => handleDeleteUserCommission(userComm.user.id)}><HiOutlineTrash /></IconButton>
                                    </Box>
                                }>
                                    <ListItemText
                                        primary={`${userComm.user.first_name} ${userComm.user.last_name} (ID: ${userComm.user.id})`}
                                        secondary={userComm.data.length > 0 ? userComm.data.map(tier => `${tier.target} زیرمجموعه: ${tier.commission_rate}%`).join(' | ') : 'هیچ سطحی تعریف نشده'}
                                    />
                                </ListItem>
                            ))
                        ) : (<Typography variant='body2' color='text.secondary'>هیچ کاربری با تنظیمات کمیسیون خاص یافت نشد.</Typography>)}
                    </List>
                )}
            </Paper>
        </>
    );
};

export default SettingsTab;